import React, {FC, useCallback, useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {UserModel} from '../../auth'
import InputField from '../components/InputField'
import Loader from '../../common/Loader'
import NormalMessage from '../../common/NormalMessage'
import {getOffers} from '../../offers/cors/_requests'
import {IOfferModel} from '../../offers/cors/_models'
import {addDailyDealProduct} from '../cors/_requests'
import {prepareErrors} from '../../errors/core/_models'

type IProps = {
    show: boolean
    onClose: () => void
    language: string
    currentUser: UserModel | undefined
    refreshData: () => Promise<void>
}

const PAGE_SIZE = 25

const fmtMoney = (v: string | number | null | undefined): string => {
    const n = typeof v === 'string' ? parseFloat(v) : v ?? 0
    if (n === null || n === undefined || Number.isNaN(n)) return '—'
    return Number(n).toFixed(2)
}

/** Square thumbnail that degrades to a neutral placeholder when the image
 *  path can't be resolved (so we never show a broken-image icon). */
const ProductThumb: FC<{src: string | null; alt: string}> = ({src, alt}) => {
    const [broken, setBroken] = useState(false)
    const box: React.CSSProperties = {
        width: 44,
        height: 44,
        borderRadius: 6,
        objectFit: 'cover',
        background: '#f1f1f4',
        border: '1px solid #eaeaef',
        flexShrink: 0,
    }
    if (!src || broken) {
        return (
            <span
                style={{...box, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#b5b5c3'}}
                aria-hidden='true'
            >
                <i className='fas fa-image' />
            </span>
        )
    }
    return <img src={src} alt={alt} style={box} loading='lazy' onError={() => setBroken(true)} />
}

/**
 * New Daily Deal flow, presented as a popup: the supplier picks from their
 * WINNING offers (image + name + base price), enters one shared
 * discount/quantity/date, and clicks Apply to stage the selected products.
 * Staging + go-live (the "Add Daily Deal" button on the staged table) reuse
 * the existing two-step backend. Replaces the old DailyDealForm barcode input.
 */
const DailyDealSelector: FC<IProps> = ({show, onClose, language, currentUser, refreshData}) => {
    const {formatMessage} = useIntl()
    const currentDate = new Date().toISOString().split('T')[0]

    const [offers, setOffers] = useState<IOfferModel[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selected, setSelected] = useState<Set<number>>(new Set())

    const emptyForm = {
        discount_date: {error: false, value: '', msg: ''},
        quantity: {error: false, value: 4, msg: ''},
        discount_percentage: {error: false, value: '', msg: ''},
        msg: '',
    }
    const [form, setForm] = useState(emptyForm)

    const fetchOffers = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await getOffers(
                {page, limit: PAGE_SIZE, winning_only: true, status: 'active', search: search || undefined},
                currentUser?.token,
                language,
            )
            setOffers(res.data?.data ?? [])
            setTotal(res.data?.total ?? 0)
        } catch {
            setOffers([])
            setTotal(0)
        }
        setIsLoading(false)
    }, [page, search, currentUser?.token, language])

    // Load (and refresh on search/page change) only while the popup is open.
    useEffect(() => {
        if (show) fetchOffers()
    }, [show, fetchOffers])

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | {target: {id: string; value: string}},
    ) => {
        const {id, value} = e.target
        setForm((prev: any) => ({...prev, [id]: {...prev[id], value}}))
    }

    const toggleSelect = (productId: number) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(productId)) next.delete(productId)
            else next.add(productId)
            return next
        })
    }

    const toggleSelectAll = () => {
        setSelected((prev) => {
            const pageIds = offers.map((o) => o.product_id)
            const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id))
            const next = new Set(prev)
            if (allSelected) pageIds.forEach((id) => next.delete(id))
            else pageIds.forEach((id) => next.add(id))
            return next
        })
    }

    const resetAndClose = () => {
        setSelected(new Set())
        setForm(emptyForm)
        setSearch('')
        setPage(1)
        onClose()
    }

    const validate = (): boolean => {
        let hasError = false
        setForm((prev: any) => ({
            ...prev,
            discount_date: {...prev.discount_date, error: false, msg: ''},
            quantity: {...prev.quantity, error: false, msg: ''},
            discount_percentage: {...prev.discount_percentage, error: false, msg: ''},
            msg: '',
        }))

        if (selected.size === 0) {
            setForm((prev: any) => ({...prev, msg: formatMessage({id: 'select_products_for_deal'})}))
            hasError = true
        }
        if (form.discount_date.value === '') {
            setForm((prev: any) => ({...prev, discount_date: {...prev.discount_date, error: true}}))
            hasError = true
        }
        if (!form.quantity.value || Number(form.quantity.value) <= 0) {
            setForm((prev: any) => ({...prev, quantity: {...prev.quantity, error: true}}))
            hasError = true
        }
        if (form.discount_percentage.value === '') {
            setForm((prev: any) => ({...prev, discount_percentage: {...prev.discount_percentage, error: true}}))
            hasError = true
        }
        return hasError
    }

    const handleApply = async () => {
        if (validate()) return
        try {
            setIsSubmitting(true)
            await addDailyDealProduct(currentUser?.token, language, {
                product_ids: Array.from(selected),
                discount_date: form.discount_date.value,
                quantity: Number(form.quantity.value),
                discount_percentage: form.discount_percentage.value,
            })
            await refreshData()
            resetAndClose()
        } catch (exp: any) {
            const updatedState: any = prepareErrors(exp, form)
            if (updatedState) setForm(updatedState)
        }
        setIsSubmitting(false)
    }

    const pageIds = offers.map((o) => o.product_id)
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
    const totalPages = Math.ceil(total / PAGE_SIZE)

    return (
        <Modal show={show} onHide={resetAndClose} size='lg' centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{formatMessage({id: 'select_products_for_deal'})}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Shared deal settings */}
                <div className='row g-4 align-items-start mb-4'>
                    <div className='col-md-4 col-sm-6'>
                        <InputField
                            type='date'
                            id='discount_date'
                            value={form.discount_date.value}
                            isRequired={true}
                            hasError={form.discount_date.error}
                            onChange={handleChange}
                            label={formatMessage({id: 'discount_date'})}
                            errorMsg={form.discount_date.msg}
                            min={currentDate}
                        />
                    </div>
                    <div className='col-md-4 col-sm-6'>
                        <InputField
                            type='number'
                            id='quantity'
                            value={form.quantity.value}
                            isRequired={true}
                            hasError={form.quantity.error}
                            onChange={handleChange}
                            label={formatMessage({id: 'quantity'})}
                            errorMsg={form.quantity.msg}
                        />
                    </div>
                    <div className='col-md-4 col-sm-6'>
                        <InputField
                            type='number'
                            id='discount_percentage'
                            value={form.discount_percentage.value}
                            isRequired={true}
                            hasError={form.discount_percentage.error}
                            onChange={handleChange}
                            label={formatMessage({id: 'discount_percentage'})}
                            errorMsg={form.discount_percentage.msg}
                        />
                    </div>
                </div>

                {/* Search + live counters (selection persists across pages). */}
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                    <div className='d-flex align-items-center gap-2 text-muted'>
                        <span className='badge badge-light-primary'>
                            {formatMessage({id: 'selected'})}: {selected.size}
                        </span>
                        <span className='fs-7'>{formatMessage({id: 'total'})}: {total}</span>
                    </div>
                    <input
                        className='form-control form-control-solid'
                        style={{maxWidth: 280}}
                        placeholder={formatMessage({id: 'search'})}
                        value={search}
                        onChange={(e) => {
                            setPage(1)
                            setSearch(e.target.value)
                        }}
                    />
                </div>

                {isLoading ? (
                    <Loader isFull={false} />
                ) : offers.length === 0 ? (
                    <NormalMessage msg={formatMessage({id: 'no_winning_offers'})} />
                ) : (
                    <>
                        <div className='table-responsive'>
                            <table className='table table-row-bordered table-row-gray-200 align-middle gy-3'>
                                <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                        <th className='ps-4 rounded-start' style={{width: 48}}>
                                            <input
                                                type='checkbox'
                                                className='form-check-input'
                                                checked={allOnPageSelected}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th style={{width: 64}}>{formatMessage({id: 'image'})}</th>
                                        <th>{formatMessage({id: 'prod_name'})}</th>
                                        <th className='text-end pe-4 rounded-end text-nowrap' style={{width: 120}}>
                                            {formatMessage({id: 'price'})}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {offers.map((offer) => {
                                        const isSel = selected.has(offer.product_id)
                                        return (
                                            <tr
                                                key={offer.product_supplier_id}
                                                onClick={() => toggleSelect(offer.product_id)}
                                                style={{
                                                    cursor: 'pointer',
                                                    background: isSel ? 'rgba(0,158,247,0.06)' : undefined,
                                                }}
                                            >
                                                <td className='ps-4' onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type='checkbox'
                                                        className='form-check-input'
                                                        checked={isSel}
                                                        onChange={() => toggleSelect(offer.product_id)}
                                                    />
                                                </td>
                                                <td>
                                                    <ProductThumb src={offer.image} alt={offer.product_name ?? ''} />
                                                </td>
                                                <td className='fw-semibold'>
                                                    {offer.product_name ?? `#${offer.product_id}`}
                                                </td>
                                                <td className='text-end pe-4 fw-bold text-nowrap'>
                                                    {fmtMoney(offer.price)}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className='d-flex justify-content-end align-items-center gap-3 mt-2'>
                                <Button
                                    variant='light'
                                    size='sm'
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    {'‹'}
                                </Button>
                                <span className='text-muted'>
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant='light'
                                    size='sm'
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    {'›'}
                                </Button>
                            </div>
                        )}
                    </>
                )}

                {form.msg && (
                    <div className='text-danger fw-semibold mt-3'>
                        <i className='fas fa-exclamation-circle me-1' /> {form.msg}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant='light' onClick={resetAndClose} disabled={isSubmitting}>
                    {formatMessage({id: 'cancel'})}
                </Button>
                <Button onClick={handleApply} disabled={isSubmitting || selected.size === 0}>
                    {formatMessage({id: 'apply'})}
                    {selected.size > 0 ? ` (${selected.size})` : ''}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default DailyDealSelector
